import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { bonusApi } from '../api/rrhh'
import Boton from '../components/Boton'
import Modal from '../components/Modal'
import { vinculoTexto, reglaTexto, BadgeVigencia } from './BonusPage'
import { fmtFecha, fmtMonto, hoyISO } from '../utils/fecha'
import { colors, text, radius, space, sx } from '../design/tokens'

const td = { padding: '10px 16px' }
const tdNum = { ...td, ...sx.mono, textAlign: 'right', whiteSpace: 'nowrap' }
const plata = (n) => (n == null ? '—' : `$ ${fmtMonto(n)}`)

/**
 * Un acuerdo de bonus: su regla, sus liquidaciones (con los eventos y los
 * números congelados al liquidar) y la liquidación nueva — se eligen los
 * informes de evento completos que todavía no se liquidaron.
 */
export default function BonusAcuerdoPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [data, setData] = useState(null)
  const [liquidando, setLiquidando] = useState(false)
  const [abierta, setAbierta] = useState(null)
  const [anulando, setAnulando] = useState(null)

  const cargar = useCallback(() => {
    bonusApi.acuerdo(id).then(({ data }) => setData(data))
  }, [id])
  useEffect(() => { cargar() }, [cargar])

  if (!data) return <p style={{ color: colors.textMeta }}>Cargando…</p>
  const { acuerdo, liquidaciones } = data

  return (
    <div>
      <Boton variante="ghost" sm style={{ marginBottom: space.md }} onClick={() => navigate(-1)}>← Volver</Boton>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: space.xl, marginBottom: space.xl }}>
        <div>
          <h1 style={{ margin: 0 }}>{acuerdo.persona}</h1>
          <div style={{ fontSize: text.title, color: colors.textSub, marginTop: space.xs }}>{acuerdo.nombre}</div>
          <div style={{ fontSize: text.sec, color: colors.textMeta, marginTop: space.xs }}>{vinculoTexto(acuerdo, data.legajo_nombre)}</div>
        </div>
        <div style={{ display: 'flex', gap: space.md }}>
          {acuerdo.activo && <Boton variante="primary" onClick={() => setLiquidando(true)}>+ Nueva liquidación</Boton>}
        </div>
      </div>

      <div style={{ ...sx.card, marginBottom: space.xl, display: 'flex', gap: space['3xl'], flexWrap: 'wrap' }}>
        <Dato label="Regla">{reglaTexto(acuerdo)}</Dato>
        <Dato label="Vigente desde"><span style={sx.mono}>{acuerdo.vigente_desde ? fmtFecha(acuerdo.vigente_desde) : '—'}</span></Dato>
        <Dato label="Estado"><BadgeVigencia activo={acuerdo.activo} /></Dato>
        {acuerdo.observaciones && <Dato label="Observaciones">{acuerdo.observaciones}</Dato>}
      </div>

      <h3 style={{ ...sx.cardTitle, marginBottom: space.md }}>Liquidaciones</h3>
      <div style={{ background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: radius.lg, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: text.body }}>
          <thead>
            <tr>
              {[['Número', 'left'], ['Fecha', 'left'], ['Eventos', 'right'], ['Total', 'right'], ['Estado', 'left'], ['Liquidó', 'left']].map(([h, a]) =>
                <th key={h} style={{ ...sx.th, textAlign: a }}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {liquidaciones.length === 0 && (
              <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center', color: colors.textMeta }}>Todavía no hay liquidaciones.</td></tr>
            )}
            {liquidaciones.map(l => {
              const anulada = l.estado === 'anulada'
              const reabiertos = l.eventos.filter(e => e.informe_reabierto).length
              return [
                <tr
                  key={l.id}
                  onClick={() => setAbierta(abierta === l.id ? null : l.id)}
                  title="Ver los eventos de la liquidación"
                  style={{ cursor: 'pointer', borderBottom: `1px solid ${colors.borderSoft}`, color: anulada ? colors.textMeta : colors.text }}
                  onMouseEnter={e => e.currentTarget.style.background = colors.bgSoft}
                  onMouseLeave={e => e.currentTarget.style.background = ''}
                >
                  <td style={{ ...td, ...sx.mono, whiteSpace: 'nowrap' }}>{abierta === l.id ? '▾' : '▸'} {l.numero}</td>
                  <td style={{ ...td, ...sx.mono, whiteSpace: 'nowrap' }}>{fmtFecha(l.fecha)}</td>
                  <td style={tdNum}>{l.eventos.length}</td>
                  <td style={{ ...tdNum, fontWeight: 600, textDecoration: anulada ? 'line-through' : 'none' }}>{plata(l.total)}</td>
                  <td style={td}>
                    <span style={{ ...sx.badge, background: anulada ? colors.borderSoft : colors.okBg, color: anulada ? colors.textSec : colors.ok }}>
                      {anulada ? 'Anulada' : 'Liquidada'}
                    </span>
                    {reabiertos > 0 && <span style={{ ...sx.badge, marginLeft: space.sm, background: colors.warnBg, color: colors.warn }}>⚠ {reabiertos} reabierto{reabiertos > 1 ? 's' : ''}</span>}
                  </td>
                  <td style={{ ...td, fontSize: text.sec, color: colors.textSec }}>{l.creado_por || '—'}</td>
                </tr>,
                abierta === l.id && (
                  <tr key={`${l.id}-d`} style={{ background: colors.bgSoft }}>
                    <td colSpan={6} style={{ padding: space.xl }}>
                      <TablaEventos eventos={l.eventos} />
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: space.xl, marginTop: space.lg, fontSize: text.sec, color: colors.textSec }}>
                        <div>
                          <div>Regla al liquidar: {reglaTexto(l)}</div>
                          {l.observaciones && <div>Observaciones: {l.observaciones}</div>}
                          {anulada && <div>Anulada {fmtFecha(l.anulada_at)}{l.anulada_por ? ` por ${l.anulada_por}` : ''}: {l.motivo_anulacion}</div>}
                          {reabiertos > 0 && <div style={{ color: colors.warn }}>⚠ Un informe se reabrió después de liquidado (NC sobre su compensación): la liquidación conserva los números del cierre anterior.</div>}
                        </div>
                        {!anulada && <Boton variante="destructive" sm onClick={() => setAnulando(l)}>Anular</Boton>}
                      </div>
                    </td>
                  </tr>
                ),
              ]
            })}
          </tbody>
        </table>
      </div>

      {liquidando && (
        <ModalLiquidar
          acuerdo={acuerdo}
          onClose={() => setLiquidando(false)}
          onLiquidado={(l) => { setLiquidando(false); setAbierta(l.id); cargar() }}
        />
      )}
      {anulando && (
        <ModalAnular
          liq={anulando}
          onClose={() => setAnulando(null)}
          onAnulada={() => { setAnulando(null); cargar() }}
        />
      )}
    </div>
  )
}

function Dato({ label, children }) {
  return (
    <div style={{ minWidth: 140 }}>
      <div style={sx.cardTitle}>{label}</div>
      <div style={{ fontSize: text.body, color: colors.text, marginTop: space.xs }}>{children}</div>
    </div>
  )
}

/** Eventos de una liquidación hecha: números congelados al liquidar. */
function TablaEventos({ eventos }) {
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: text.body, background: colors.bg, border: `1px solid ${colors.border}` }}>
      <thead>
        <tr>
          {[['Informe', 'left'], ['Evento', 'left'], ['Balance', 'right'], ['Vinos s/IVA', 'right'], ['Com. balance', 'right'], ['Com. vinos', 'right'], ['Total', 'right']].map(([h, a]) =>
            <th key={h} style={{ ...sx.th, textAlign: a }}>{h}</th>)}
        </tr>
      </thead>
      <tbody>
        {eventos.map(e => (
          <tr key={e.numero} style={{ borderBottom: `1px solid ${colors.borderSoft}` }}>
            <td style={{ ...td, ...sx.mono, whiteSpace: 'nowrap' }}>
              {e.numero}{e.informe_reabierto && <span title="El informe se reabrió después de liquidado" style={{ color: colors.warnStrong }}> ⚠</span>}
              <div style={{ fontSize: text.sec, color: colors.textMeta }}>{fmtFecha(e.fecha)}</div>
            </td>
            <td style={td}>{e.nombre}</td>
            <td style={{ ...tdNum, color: e.balance < 0 ? colors.err : colors.text }}>{plata(e.balance)}</td>
            <td style={tdNum}>{plata(e.vinos_sin_iva)}</td>
            <td style={tdNum}>{plata(e.comision_balance)}</td>
            <td style={tdNum}>{plata(e.comision_vinos)}</td>
            <td style={{ ...tdNum, fontWeight: 600 }}>{plata(e.total)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

/** Liquidación nueva: se eligen los eventos completos que todavía no se liquidaron. */
function ModalLiquidar({ acuerdo, onClose, onLiquidado }) {
  const [eventos, setEventos] = useState(null)
  const [elegidos, setElegidos] = useState(new Set())
  const [fecha, setFecha] = useState(hoyISO())
  const [observaciones, setObservaciones] = useState('')
  const [err, setErr] = useState('')
  const [guardando, setGuardando] = useState(false)

  useEffect(() => {
    bonusApi.eventosDisponibles(acuerdo.id).then(({ data }) => setEventos(data))
  }, [acuerdo.id])

  const alternar = (ev) => {
    if (ev.sin_dato) return
    setElegidos(s => { const n = new Set(s); n.has(ev.id) ? n.delete(ev.id) : n.add(ev.id); return n })
  }
  const seleccion = (eventos || []).filter(e => elegidos.has(e.id))
  const total = seleccion.reduce((s, e) => s + e.total, 0)

  const liquidar = async () => {
    setErr('')
    setGuardando(true)
    try {
      const { data } = await bonusApi.liquidar(acuerdo.id, { informes: [...elegidos], fecha, observaciones: observaciones || null })
      onLiquidado(data)
    } catch (error) {
      setErr(error.response?.data?.message || 'No se pudo liquidar.')
    } finally {
      setGuardando(false)
    }
  }

  return (
    <Modal title={`Nueva liquidación — ${acuerdo.persona}`} onClose={onClose} maxWidth="980px">
      <p style={{ margin: `0 0 ${space.lg}px`, fontSize: text.sec, color: colors.textSec }}>
        Informes de evento completos (compensación facturada) que todavía no se liquidaron en este acuerdo. Elegí los que forman parte del acuerdo: una vez liquidados no se pueden usar en otra liquidación.
      </p>
      <div style={{ border: `1px solid ${colors.border}`, borderRadius: radius.md, overflow: 'auto', maxHeight: '50vh' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: text.body }}>
          <thead>
            <tr>
              {[['', 'left'], ['Informe', 'left'], ['Evento', 'left'], ['Balance', 'right'], ['Vinos s/IVA', 'right'], ['Com. balance', 'right'], ['Com. vinos', 'right'], ['Total', 'right']].map(([h, a], i) =>
                <th key={i} style={{ ...sx.th, textAlign: a, position: 'sticky', top: 0 }}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {eventos === null && <tr><td colSpan={8} style={{ padding: 24, textAlign: 'center', color: colors.textMeta }}>Cargando…</td></tr>}
            {eventos?.length === 0 && <tr><td colSpan={8} style={{ padding: 24, textAlign: 'center', color: colors.textMeta }}>No hay eventos completos sin liquidar.</td></tr>}
            {eventos?.map(e => (
              <tr
                key={e.id}
                onClick={() => alternar(e)}
                style={{
                  cursor: e.sin_dato ? 'not-allowed' : 'pointer', borderBottom: `1px solid ${colors.borderSoft}`,
                  background: elegidos.has(e.id) ? colors.linkBg : '', color: e.sin_dato ? colors.textMeta : colors.text,
                }}
              >
                <td style={{ ...td, width: 24 }}><input type="checkbox" checked={elegidos.has(e.id)} disabled={e.sin_dato} readOnly /></td>
                <td style={{ ...td, ...sx.mono, whiteSpace: 'nowrap' }}>
                  {e.numero}
                  <div style={{ fontSize: text.sec, color: colors.textMeta }}>{fmtFecha(e.fecha)}</div>
                </td>
                <td style={td}>
                  {e.nombre}
                  <div style={{ fontSize: text.sec, color: colors.textMeta }}>
                    {e.sin_dato && <span style={{ color: colors.warn }}>Sin balance en el cierre del informe: revisalo antes de liquidar. </span>}
                    {e.vinos_sin_precio > 0 && `${e.vinos_sin_precio} vino${e.vinos_sin_precio > 1 ? 's' : ''} sin precio (no paga${e.vinos_sin_precio > 1 ? 'n' : ''}). `}
                    {!e.sin_dato && e.balance <= 0 && 'Balance no positivo: no paga comisión. '}
                    {e.antes_de_vigencia && 'Anterior a la vigencia del acuerdo.'}
                  </div>
                </td>
                <td style={{ ...tdNum, color: e.balance < 0 ? colors.err : undefined }}>{plata(e.balance)}</td>
                <td style={tdNum}>{plata(e.vinos_sin_iva)}</td>
                <td style={tdNum}>{plata(e.comision_balance)}</td>
                <td style={tdNum}>{plata(e.comision_vinos)}</td>
                <td style={{ ...tdNum, fontWeight: 600 }}>{plata(e.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', gap: space.xl, alignItems: 'flex-end', marginTop: space.lg, flexWrap: 'wrap' }}>
        <div>
          <label style={st.lbl}>Fecha</label>
          <input type="date" style={sx.input} value={fecha} onChange={e => setFecha(e.target.value)} />
        </div>
        <div style={{ flex: 1, minWidth: 220 }}>
          <label style={st.lbl}>Observaciones</label>
          <input style={{ ...sx.input, width: '100%', boxSizing: 'border-box' }} value={observaciones} onChange={e => setObservaciones(e.target.value)} />
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={sx.cardTitle}>{seleccion.length} evento{seleccion.length === 1 ? '' : 's'} · total</div>
          <div style={{ ...sx.mono, fontSize: text.title, fontWeight: 700 }}>{plata(total)}</div>
        </div>
      </div>

      {err && <div style={{ color: colors.errStrong, fontSize: text.sec, marginTop: space.lg }}>{err}</div>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: space.md, marginTop: space.xl }}>
        <Boton variante="ghost" onClick={onClose}>Cancelar</Boton>
        <Boton variante="primary" disabled={guardando || seleccion.length === 0} onClick={liquidar}>
          {guardando ? 'Liquidando…' : 'Liquidar'}
        </Boton>
      </div>
    </Modal>
  )
}

function ModalAnular({ liq, onClose, onAnulada }) {
  const [motivo, setMotivo] = useState('')
  const [err, setErr] = useState('')
  const [anulandoAhora, setAnulandoAhora] = useState(false)

  const anular = async () => {
    setErr('')
    setAnulandoAhora(true)
    try {
      await bonusApi.anular(liq.id, motivo)
      onAnulada()
    } catch (error) {
      setErr(error.response?.data?.message || 'No se pudo anular.')
    } finally {
      setAnulandoAhora(false)
    }
  }

  return (
    <Modal title={`Anular ${liq.numero}`} onClose={onClose}>
      <p style={{ margin: 0, fontSize: text.body, color: colors.textSub }}>
        La liquidación queda en el historial como anulada y sus {liq.eventos.length} evento{liq.eventos.length === 1 ? '' : 's'} vuelven a estar disponibles para liquidar.
      </p>
      <label style={st.lbl}>Motivo</label>
      <textarea style={{ ...sx.input, width: '100%', boxSizing: 'border-box', minHeight: 70 }} value={motivo} onChange={e => setMotivo(e.target.value)} autoFocus />
      {err && <div style={{ color: colors.errStrong, fontSize: text.sec, marginTop: space.md }}>{err}</div>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: space.md, marginTop: space.xl }}>
        <Boton variante="ghost" onClick={onClose}>Cancelar</Boton>
        <Boton variante="destructive" disabled={!motivo.trim() || anulandoAhora} onClick={anular}>{anulandoAhora ? 'Anulando…' : 'Anular liquidación'}</Boton>
      </div>
    </Modal>
  )
}

const st = {
  lbl: { display: 'block', fontSize: text.sec, fontWeight: 600, color: colors.textSub, margin: `${space.lg}px 0 ${space.xs}px` },
}
